import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProviderInvitationsPanel } from '../components/ProviderInvitationsPanel';
import { adminApi } from '../services/api';
import type { AdminInspector } from '../types';

type SortDirection = 'asc' | 'desc';
type SortField = keyof AdminInspector | 'companyNumber' | 'companyName' | 'age';

interface ColumnDef {
  field: SortField;
  label: string;
}

const DEFAULT_COLUMN_ORDER: SortField[] = [
  'inspectorNumber', 'verifiedInspector', 'name', 'organizationNumber',
  'inspectorCompanyName', 'phone', 'vatNumber', 'email',
  'website',
  'address', 'postalCode', 'city', 'createdAt'
];

interface ColumnFilter {
  [key: string]: string;
}

export function AdminInspectorList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inspectors, setInspectors] = useState<AdminInspector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInspectorId, setSelectedInspectorId] = useState<string | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('inspectorNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter state
  const [filters, setFilters] = useState<ColumnFilter>({});

  // Column order state
  const [columnOrder, setColumnOrder] = useState<SortField[]>(DEFAULT_COLUMN_ORDER);
  const [draggedColumn, setDraggedColumn] = useState<SortField | null>(null);

  useEffect(() => {
    loadData();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await adminApi.getPreferences();
      if (prefs.adminInspectorListColumns && Array.isArray(prefs.adminInspectorListColumns)) {
        const validColumns = prefs.adminInspectorListColumns.filter(
          (col: string) => DEFAULT_COLUMN_ORDER.includes(col as SortField)
        );
        const newColumns = DEFAULT_COLUMN_ORDER.filter(
          col => !validColumns.includes(col)
        );
        setColumnOrder([...validColumns, ...newColumns] as SortField[]);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const saveColumnOrder = useCallback(async (newOrder: SortField[]) => {
    try {
      await adminApi.savePreference('adminInspectorListColumns', newOrder);
    } catch (error) {
      console.error('Failed to save column order:', error);
    }
  }, []);

  const loadData = async () => {
    try {
      const inspectorsData = await adminApi.getAllInspectors();
      setInspectors(inspectorsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const calculateAgeDays = (dateStr: string): number => {
    if (!dateStr) return 0;
    try {
      const created = new Date(dateStr);
      const today = new Date();
      const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate());
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const diffTime = todayDate.getTime() - createdDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return 0;
    }
  };

  const getVerificationStatus = (inspector: AdminInspector) => {
    if (inspector.verifiedInspector === 'Verifierad') {
      return {
        label: t('inspectors.status.verified'),
        className: 'bg-success'
      };
    }
    return {
      label: t('inspectors.status.notVerified'),
      className: 'bg-warning'
    };
  };

  const filteredAndSortedInspectors = useMemo(() => {
    let result = [...inspectors];

    // Apply filters
    Object.entries(filters).forEach(([field, value]) => {
      if (value) {
        result = result.filter((inspector) => {
          const fieldValue = (inspector as any)[field];
          if (field === 'verifiedInspector') {
            return String(fieldValue) === value;
          }
          return String(fieldValue || '').toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    // Apply sorting
    result.sort((a, b) => {
      if (sortField === 'age') {
        const aAge = calculateAgeDays(a.createdAt);
        const bAge = calculateAgeDays(b.createdAt);
        return sortDirection === 'asc' ? aAge - bAge : bAge - aAge;
      }

      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return result;
  }, [inspectors, filters, sortField, sortDirection]);

  const handleRowClick = (inspectorId: string) => {
    setSelectedInspectorId(selectedInspectorId === inspectorId ? null : inspectorId);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <i className="feather-chevrons-up-down ms-1 opacity-25"></i>;
    }
    return sortDirection === 'asc' ? (
      <i className="feather-chevron-up ms-1"></i>
    ) : (
      <i className="feather-chevron-down ms-1"></i>
    );
  };

  const formatDateTime = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  const columnLabels: Record<SortField, string> = {
    companyNumber: t('adminInspectors.columns.companyNumber'),
    companyName: t('adminInspectors.columns.companyName'),
    inspectorNumber: t('inspectors.columns.inspectorNumber'),
    userId: 'User ID',
    name: t('inspectors.columns.name'),
    organizationNumber: t('inspectors.columns.organizationNumber'),
    inspectorCompanyName: t('inspectors.columns.inspectorCompanyName'),
    email: t('inspectors.columns.email'),
    phone: t('inspectors.columns.phone'),
    vatNumber: t('inspectors.columns.vatNumber'),
    website: t('inspectors.columns.website'),
    inspectorPresentation: t('inspectors.columns.inspectorPresentation'),
    verifiedInspector: t('inspectors.columns.verifiedInspector'),
    address: t('inspectors.columns.address'),
    postalCode: t('inspectors.columns.postalCode'),
    city: t('inspectors.columns.city'),
    country: t('inspectors.columns.country', 'Land'),
    title: t('inspectors.columns.title', 'Titel'),
    experienceYears: t('inspectors.columns.experienceYears', 'Erfarenhetsår'),
    specialties: t('inspectors.columns.specialties', 'Specialistområden'),
    responseTime: t('inspectors.columns.responseTime', 'Svarstid'),
    hourlyRate: t('inspectors.columns.hourlyRate', 'Timarvode'),
    fixedPriceAvailable: t('inspectors.columns.fixedPriceAvailable', 'Fast pris'),
    geographicArea: t('inspectors.columns.geographicArea', 'Geografiskt område'),
    languages: t('inspectors.columns.languages', 'Språk'),
    initialMeetingFree: t('inspectors.columns.initialMeetingFree', 'Gratis möte'),
    availability: t('inspectors.columns.availability', 'Tillgänglig'),
    leadTime: t('inspectors.columns.leadTime', 'Ledtid'),
    notes: t('inspectors.columns.notes'),
    linkedCustomerIds: t('inspectors.columns.linkedCustomers', 'Kopplade beställare'),
    linkedCompanyNumbers: t('inspectors.columns.linkedCompanies', 'Kopplade hantverkare'),
    createdAt: t('adminInspectors.columns.createdAt'),
    age: t('adminInspectors.columns.age'),
    id: 'ID',
    updatedAt: t('adminInspectors.columns.createdAt')
  };

  const columns: ColumnDef[] = columnOrder.map(field => ({
    field,
    label: columnLabels[field] || field
  }));

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, field: SortField) => {
    setDraggedColumn(field);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetField: SortField) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetField) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetField);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    saveColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const exportToExcel = () => {
    const headers = columnOrder.map(field => columnLabels[field] || field);

    const getCellValue = (inspector: AdminInspector, field: SortField): string | number => {
      if (field === 'createdAt') return formatDateTime(inspector.createdAt);
      if (field === 'age') return calculateAgeDays(inspector.createdAt);
      return (inspector as any)[field] || '';
    };

    const rows = filteredAndSortedInspectors.map(inspector =>
      columnOrder.map(field => getCellValue(inspector, field))
    );

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `admin_inspectors_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminInspectors.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminInspectors.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminInspectors.description')}</p>
        </div>
      </div>

      <ProviderInvitationsPanel type="inspector" />

      <Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-2">
            <Link to="/admin/inspectors/create" className="btn btn-primary">
              <i className="feather-plus"></i>
              <span className="d-none d-sm-inline ms-2">{t('inspectors.createInspector')}</span>
            </Link>
            {selectedInspectorId ? (
              <Button variant="light-brand" onClick={() => navigate(`/admin/inspectors/view/${selectedInspectorId}`)}>
                <i className="feather-eye"></i>
                <span className="d-none d-sm-inline ms-2">{t('inspectors.editInspector')}</span>
              </Button>
            ) : (
              <Button variant="light-brand" disabled>
                <i className="feather-eye"></i>
                <span className="d-none d-sm-inline ms-2">{t('inspectors.editInspector')}</span>
              </Button>
            )}
            <Button variant="light-brand" onClick={exportToExcel}>
              <i className="feather-download"></i>
              <span className="d-none d-sm-inline ms-2">{t('inspectors.export')}</span>
            </Button>
          </div>
        </div>

        <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.field}
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.field)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, col.field)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleSort(col.field)}
                    style={{
                      cursor: 'grab',
                      whiteSpace: 'nowrap',
                      backgroundColor: draggedColumn === col.field ? '#e3f2fd' : undefined,
                      transition: 'background-color 0.2s'
                    }}
                    title={t('adminInspectors.dragToReorder')}
                  >
                    <i className="feather-move me-1" style={{ opacity: 0.4, fontSize: '12px' }}></i>
                    {col.label}
                    {renderSortIcon(col.field)}
                  </th>
                ))}
              </tr>
              <tr>
                {columns.map((col) => (
                  <th key={`filter-${col.field}`} className="p-1">
                    {col.field === 'verifiedInspector' ? (
                      <select
                        className="form-select form-select-sm"
                        value={filters[col.field] || ''}
                        onChange={(e) => handleFilterChange(col.field, e.target.value)}
                      >
                        <option value="">{t('inspectors.allStatuses')}</option>
                        <option value="Ej Verifierad">{t('inspectors.status.notVerified')}</option>
                        <option value="Verifierad">{t('inspectors.status.verified')}</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t('inspectors.search')}
                        value={filters[col.field] || ''}
                        onChange={(e) => handleFilterChange(col.field, e.target.value)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedInspectors.length === 0 ? (
                <tr>
                  <td colSpan={columnOrder.length} className="text-center text-muted py-4">
                    {inspectors.length === 0 ? t('inspectors.noInspectors') : t('projects.noResults')}
                  </td>
                </tr>
              ) : (
                filteredAndSortedInspectors.map((inspector) => {
                  const verificationStatus = getVerificationStatus(inspector);
                  return (
                    <tr
                      key={inspector.id}
                      onClick={() => handleRowClick(inspector.id)}
                      onDoubleClick={() => navigate(`/admin/inspectors/view/${inspector.id}`)}
                      className={selectedInspectorId === inspector.id ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      {columnOrder.map((field) => {
                        if (field === 'createdAt') {
                          return <td key={field}>{formatDateTime(inspector.createdAt)}</td>;
                        }
                        if (field === 'age') {
                          return <td key={field}>{calculateAgeDays(inspector.createdAt)}</td>;
                        }
                        if (field === 'verifiedInspector') {
                          return (
                            <td key={field}>
                              <span className={`badge ${verificationStatus.className}`}>
                                {verificationStatus.label}
                              </span>
                            </td>
                          );
                        }
                        return <td key={field}>{(inspector as any)[field]}</td>;
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </div>

        <div className="d-md-none">
          <div className="mb-3">
            <div className="row g-2">
              <div className="col-8">
                <input type="text" className="form-control form-control-sm" placeholder={t('inspectors.search')} value={filters['name'] || ''} onChange={(e) => handleFilterChange('name', e.target.value)} />
              </div>
              <div className="col-4">
                <select className="form-select form-select-sm" value={filters['verifiedInspector'] || ''} onChange={(e) => handleFilterChange('verifiedInspector', e.target.value)}>
                  <option value="">{t('inspectors.allStatuses')}</option>
                  <option value="Ej Verifierad">{t('inspectors.status.notVerified')}</option>
                  <option value="Verifierad">{t('inspectors.status.verified')}</option>
                </select>
              </div>
            </div>
          </div>
          {filteredAndSortedInspectors.length === 0 ? (
            <p className="text-muted text-center py-3">{inspectors.length === 0 ? t('inspectors.noInspectors') : t('projects.noResults')}</p>
          ) : (
            filteredAndSortedInspectors.map((inspector) => {
              const verificationStatus = getVerificationStatus(inspector);
              const isSelected = selectedInspectorId === inspector.id;
              return (
                <div key={inspector.id} className={`card mb-2 ${isSelected ? 'border-primary' : ''}`} onClick={() => handleRowClick(inspector.id)} onDoubleClick={() => navigate(`/admin/inspectors/view/${inspector.id}`)} style={{ cursor: 'pointer' }}>
                  <div className="card-body py-2 px-3">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-semibold text-truncate">#{inspector.inspectorNumber} {inspector.name}</span>
                        <span className={`badge ${verificationStatus.className} flex-shrink-0`}>{verificationStatus.label}</span>
                      </div>
                      {inspector.inspectorCompanyName && (
                        <div className="text-muted small"><i className="feather-briefcase me-1"></i>{inspector.inspectorCompanyName}</div>
                      )}
                      {inspector.email && (
                        <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{inspector.email}</div>
                      )}
                      {inspector.phone && (
                        <div className="text-muted small"><i className="feather-phone me-1"></i>{inspector.phone}</div>
                      )}
                      {(inspector.address || inspector.city) && (
                        <div className="text-muted small text-truncate"><i className="feather-map-pin me-1"></i>{[inspector.address, inspector.postalCode, inspector.city].filter(Boolean).join(', ')}</div>
                      )}
                      <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{formatDateTime(inspector.createdAt)}</div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>
    </DashboardLayout>
  );
}
